// src/pages/StudentsTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2 } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl } from "@fortawesome/free-solid-svg-icons";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const StudentsTable = () => {
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const t = (k, d) => translations[k] || d;

  const [searchParams, setSearchParams] = useSearchParams();

  // filtre dropdown değerleri
  const [opticalForms, setOpticalForms]       = useState([]);
  const [schools, setSchools]                 = useState([]);
  const [classes, setClasses]                 = useState([]);
  const [educationYears, setEducationYears]   = useState([]);

  // seçili filtreler
  const [selectedOpticalForm, setSelectedOpticalForm] = useState("");
  const [selectedSchool,      setSelectedSchool]      = useState("");
  const [selectedClass,       setSelectedClass]       = useState("");
  const [selectedYear,        setSelectedYear]        = useState("");

  const [students, setStudents] = useState([]);
  const [filtered, setFiltered] = useState(false);

  // 1️⃣ URL’den parametreleri oku
  useEffect(() => {
    const ofId    = searchParams.get("opticalFormId")    || "";
    const instId  = searchParams.get("institutionId")   || "";
    const clsId   = searchParams.get("classId")         || "";
    const yrId    = searchParams.get("educationYearId") || "";

    setSelectedOpticalForm(ofId);
    setSelectedSchool(instId);
    setSelectedClass(clsId);
    setSelectedYear(yrId);

    // parametrik fetch
    if (ofId || instId || clsId || yrId) {
      fetchStudents(ofId, instId, clsId, yrId);
    }
  }, []); // yalnızca mount

  // 2️⃣ filtre dropdown’larını doldur
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const cfg   = { headers: { Authorization: `Bearer ${token}` } };

    Promise.all([
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/optical-forms/list`, cfg),
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/institution/list`,   cfg),
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/class/list`,         cfg),
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/education-years/list`, cfg),
    ])
    .then(([ofR, schR, clsR, yrR]) => {
      setOpticalForms(ofR.data);
      setSchools(schR.data);
      setClasses(clsR.data);
      setEducationYears(yrR.data);
    })
    .catch(err => console.error("❌ Filtre verileri alınamadı:", err));
  }, []);

  // 3️⃣ öğrencileri çek
  const fetchStudents = async (ofId, instId, clsId, yrId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/form-reader-results`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            opticalFormId: ofId   ?? selectedOpticalForm,
            institutionId: instId ?? selectedSchool,
            classId      : clsId  ?? selectedClass,
            educationYearId: yrId ?? selectedYear,
          }
        }
      );
      // uniq öğrenciler
      const seen = new Set();
      const uniq = data.filter(item => {
        if (seen.has(item.student_id)) return false;
        seen.add(item.student_id);
        return true;
      });
      setStudents(uniq);
      setFiltered(true);
    } catch (err) {
      console.error("❌ Öğrenci verisi alınamadı:", err);
    }
  };

  // 4️⃣ filtrele butonu
  const handleFilterClick = () => {
    const params = {};
    if (selectedOpticalForm) params.opticalFormId    = selectedOpticalForm;
    if (selectedSchool)      params.institutionId    = selectedSchool;
    if (selectedClass)       params.classId          = selectedClass;
    if (selectedYear)        params.educationYearId  = selectedYear;

    setSearchParams(params);
    fetchStudents();
  };

  // 5️⃣ soft delete isteği
  const handleSoftDelete = (id) => {
    confirmAlert({
      title: t("CONFIRM_DELETE","Silmek istediğinize emin misiniz?"),
      message: t("DELETE_STUDENT_MSG","Bu öğrenci kaydı silinecek."),
      buttons: [
        {
          label: t("YES","Evet"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/students/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(t("DELETED","Silindi"));
              fetchStudents();
            } catch (e) {
              console.error("❌ Silme hatası:", e);
              toast.error(t("DELETE_FAILED","Silme başarısız"));
            }
          }
        },
        { label: t("CANCEL","İptal"), onClick: () => {} }
      ]
    });
  };

  // 6️⃣ tablo kolonları
  const columns = useMemo(() => [
    { accessorKey: "student_id",    header: t("STUDENT_ID","Öğrenci ID"), size: 80 },
    { accessorKey: "school_no",     header: t("SCHOOL_NO","Okul No") },
    { accessorKey: "first_name",    header: t("FIRST_NAME","Ad") },
    { accessorKey: "last_name",     header: t("LAST_NAME","Soyad") },
    { accessorKey: "tc_kimlik_no",  header: t("TC_NO","TC Kimlik No") },
    { accessorKey: "class_name",    header: t("CLASS","Sınıf") },
    { accessorKey: "branch",        header: t("BRANCH","Şube") },
    { accessorKey: "phone",         header: t("PHONE","Telefon") },
    {
      accessorKey: "created_at",
      header: t("CREATED_AT","Oluşturulma"),
      cell: info => new Date(info.getValue()).toLocaleDateString("tr-TR")
    },
    {
      accessorKey: "actions",
      header: t("ACTIONS","İşlemler"),
      enableSorting: false,
      cell: info => {
        const sid = info.row.original.student_id;
        return (
          <div className="hstack gap-2 justify-content-end">
            <Link to={`/students/view/${sid}`} className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary" title={t("VIEW","Görüntüle")}>
              <FiEye/>
            </Link>
            <Link to={`/optical-form/students/answer/${sid}`} className="avatar-text avatar-md text-warning bg-soft-warning border-soft-warning" title={t("ANSWERS","Cevaplar")}>
              <FontAwesomeIcon icon={faListUl}/>
            </Link>
            <Link to={`/optical-form/students/edit/${sid}`} className="avatar-text avatar-md text-success bg-soft-success border-soft-success" title={t("EDIT","Düzenle")}>
              <FiEdit3/>
            </Link>
            <button onClick={() => handleSoftDelete(sid)} className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger" title={t("DELETE","Sil")}>
              <FiTrash2/>
            </button>
          </div>
        );
      }
    }
  ], [translations, students]);

  return (
    <div className="container py-3">
      {/* ► Filtreleme */}
      <div className={`filter-section bg-white p-3 rounded shadow-sm mb-4 ${filtered ? "sticky-top" : ""}`}>
        <div className="d-flex gap-2">
          <select className="form-select" value={selectedOpticalForm} onChange={e=>setSelectedOpticalForm(e.target.value)}>
            <option value="">{t("SELECT_FORM","Optik Form Seçin")}</option>
            {opticalForms.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
          </select>
          <select className="form-select" value={selectedSchool} onChange={e=>setSelectedSchool(e.target.value)}>
            <option value="">{t("SELECT_SCHOOL","Kurum Seçin")}</option>
            {schools.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <select className="form-select" value={selectedClass} onChange={e=>setSelectedClass(e.target.value)}>
            <option value="">{t("SELECT_CLASS","Sınıf Seçin")}</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.title||c.name}</option>)}
          </select>
          {/* ▼ Eğitim yılı dropdown */}
          <select className="form-select" value={selectedYear} onChange={e=>setSelectedYear(e.target.value)}>
            <option value="">{t("SELECT_EDU_YEAR","Yıl Seçin")}</option>
            {educationYears.map(y => (
              <option key={y.id} value={y.id}>{y.title}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={handleFilterClick}>{t("FILTER","Filtrele")}</button>
        </div>
      </div>

      {filtered && <Table data={students} columns={columns} />}
    </div>
  );
};

export default StudentsTable;
